import { zodResolver } from "@hookform/resolvers/zod";
import { useState, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

import { useCreateUser } from "../api/use-create-user";
import { useUpdateUser } from "../api/use-update-user";
import { ROLES, userFormSchema, type UserFormValues } from "../lib/user-form-schema";

interface EditTarget {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
}

interface UserFormModalProps {
  mode: "create" | "edit";
  editTarget?: EditTarget;
  onClose: () => void;
}

interface CreatedUser {
  displayName: string;
  tempPassword: string;
}

const FIELD_CLASS =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-ring disabled:opacity-50";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold text-muted-foreground";
const ERROR_CLASS = "mt-1 text-xs text-destructive";

export function UserFormModal({ mode, editTarget, onClose }: UserFormModalProps): JSX.Element {
  const { t } = useTranslation("users");
  const create = useCreateUser();
  const update = useUpdateUser();
  const isPending = mode === "create" ? create.isPending : update.isPending;
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: editTarget?.email ?? "",
      display_name: editTarget?.display_name ?? "",
      role_names: editTarget?.roles ?? [],
    },
  });

  // useWatch is the React-Compiler-safe way to subscribe to a single field —
  // useForm().watch() cannot be memoized.
  const selectedRoles = useWatch({ control, name: "role_names" });

  function toggleRole(value: string): void {
    const next = selectedRoles.includes(value)
      ? selectedRoles.filter((r) => r !== value)
      : [...selectedRoles, value];
    setValue("role_names", next, { shouldValidate: true });
  }

  function onSubmit(values: UserFormValues): void {
    if (mode === "create") {
      create.mutate(values, {
        onSuccess: (data) =>
          setCreatedUser({ displayName: data.user.display_name, tempPassword: data.temp_password }),
        onError: (err) => toast.error(apiErrorMessage(err)),
      });
      return;
    }
    if (!editTarget) return;
    update.mutate(
      { id: editTarget.id, body: { email: values.email, display_name: values.display_name } },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("edit.success"));
        },
        onError: (err) => toast.error(apiErrorMessage(err)),
      },
    );
  }

  if (createdUser) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t("create.success_title")}
        footer={
          <Button onClick={onClose} data-testid="user-form-created-done">
            {t("create.close")}
          </Button>
        }
      >
        <p className="mb-3 text-sm text-muted-foreground">
          {t("create.success_description", { name: createdUser.displayName })}
        </p>
        <code
          className="block rounded bg-muted px-3 py-2 text-sm"
          data-testid="user-form-created-temp"
        >
          {createdUser.tempPassword}
        </code>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === "create" ? t("create.title") : t("edit.title")}
      subtitle={mode === "create" ? t("create.subtitle") : undefined}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            data-testid="user-form-cancel"
          >
            {t("action.cancel")}
          </Button>
          <Button
            type="submit"
            form="user-form"
            disabled={isPending}
            data-testid="user-form-submit"
          >
            {mode === "create" ? t("create.submit") : t("action.save")}
          </Button>
        </>
      }
    >
      <form
        id="user-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid="user-form"
        className="space-y-4"
      >
        <div>
          <label htmlFor="user-email" className={LABEL_CLASS}>
            {t("create.email_label")}
          </label>
          <input
            id="user-email"
            type="email"
            data-testid="user-form-email"
            placeholder={t("create.email_placeholder")}
            disabled={isPending}
            className={FIELD_CLASS}
            {...register("email")}
          />
          {errors.email && (
            <p className={ERROR_CLASS} data-testid="user-form-email-error">
              {t(errors.email.message ?? "create.err_email_required")}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="user-name" className={LABEL_CLASS}>
            {t("create.name_label")}
          </label>
          <input
            id="user-name"
            type="text"
            data-testid="user-form-name"
            placeholder={t("create.name_placeholder")}
            disabled={isPending}
            className={FIELD_CLASS}
            {...register("display_name")}
          />
          {errors.display_name && (
            <p className={ERROR_CLASS} data-testid="user-form-name-error">
              {t(errors.display_name.message ?? "create.err_name_required")}
            </p>
          )}
        </div>

        {mode === "create" && (
          <div>
            <span className={LABEL_CLASS}>{t("create.roles_label")}</span>
            <div className="flex flex-col gap-2" data-testid="user-form-roles">
              {ROLES.map((role) => (
                <label key={role.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    disabled={isPending}
                    data-testid={`user-form-role-${role.value}`}
                  />
                  {t(role.labelKey)}
                </label>
              ))}
            </div>
            {errors.role_names && (
              <p className={ERROR_CLASS} data-testid="user-form-roles-error">
                {t(errors.role_names.message ?? "create.err_roles_required")}
              </p>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}
